// AchatsMobile.jsx — onglets « Achat » et « Bon de commande » en version mobile.
// Un seul écran, paramétré par `orderType` — exactement comme le composant desktop
// (GestionBonCommande n'est qu'un wrapper de GestionAchat avec orderType="purchase_order").
//
// Consultation et saisie mobile : le formulaire métier est partagé avec le desktop.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import {
  ProductScreen, Appbar, Summary, SearchBar, Chips, EmptyState, Sheet, Fab,
  ActionRow, DetailRow, Note, Pill, Mono, Ico, fmt, fmtMoney, useMobileMenu,
} from "../produits/productMobileKit";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import { api, PurchaseFormWorkspace } from "../../gestion-achat/GestionAchat";
import useBusinessMutationInvalidation from "../../hooks/useBusinessMutationInvalidation";
import { PURCHASE_TABS } from "../../gestion-achat/PurchaseTabsLayout";
import PaymentFormModal from "../../gestion-achat/components/PaymentFormModal";
import { downloadOrderPdf, fetchOrderPayments } from "./achatsMobileApi";
import {
  destructiveActionFor, extraCostsOf, fmtDate, num, orderPill, paymentPill,
  permsFor, supplierNameOf,
} from "./achatsMobileShared";

const PAGE = 20;

// Mêmes valeurs que les onglets du desktop (GestionAchat.tsx:4649-4653). Le dernier
// filtre reprend le bouton « Afficher les commandes supprimées » : `include_deleted`
// renvoie les supprimées EN PLUS des actives — on ne filtre donc pas côté client,
// sinon la pagination serveur les rendrait invisibles au-delà de la première page.
const PAYMENT_FILTERS = [
  { key: "all", label: "Tous", status: null },
  { key: "unpaid", label: "Non payés", status: "Non payé" },
  { key: "partially_paid", label: "Partiels", status: "Partiellement payé" },
  { key: "paid", label: "Payés", status: "Payé" },
];
const DELETED_FILTER = { key: "deleted", label: "Avec supprimés", status: null, deleted: true };

const idOf = (v) => (v && typeof v === "object" ? v.id : v);

export default function AchatsMobile({
  orderType = "purchase",
  moduleLabel = "achat",
  moduleLabelPlural = "achats",
  onMenu: onMenuProp,
}) {
  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();

  const companyId = idOf(selectedCompany);
  const warehouseId = idOf(selectedWarehouse);
  const pageScope = JSON.stringify([user?.id, companyId, warehouseId, orderType]);
  const currentPageScope = useRef(pageScope);
  currentPageScope.current = pageScope;

  // Les bons de commande n'ont pas de suivi de paiement (showPaymentColumns du desktop).
  const showPayments = orderType !== "purchase_order";
  const perms = permsFor(orderType);
  const canDelete = hasPermission(perms.delete);
  const canViewPayments =
    showPayments && hasPermission(perms.viewPayments);
  const canCreatePayment =
    showPayments && hasPermission(perms.createPayment);

  const filters = useMemo(
    () => (showPayments ? [...PAYMENT_FILTERS, DELETED_FILTER] : [PAYMENT_FILTERS[0], DELETED_FILTER]),
    [showPayments]
  );

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fournisseurs, setFournisseurs] = useState([]);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const [navOpen, setNavOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [paymentFor, setPaymentFor] = useState(null);
  const [formFor, setFormFor] = useState(null);
  const invalidatePurchases = useBusinessMutationInvalidation("purchase");
  const editRequestRef = useRef(0);
  useEffect(() => { editRequestRef.current += 1; setFormFor(null); setDetail(null); setItems([]); setPayments([]); }, [companyId, warehouseId, user?.id, orderType]);

  const debounceRef = useRef(null);
  const listRequestRef = useRef(0);

  const otherTabs = useMemo(
    () => PURCHASE_TABS.filter((t) => hasPermission(t.permission) && !t.path.endsWith(orderType === "purchase_order" ? "/bon-commande" : "/achat")),
    [hasPermission, orderType]
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setAppliedSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  useEffect(() => {
    if (!warehouseId) { setFournisseurs([]); return; }
    let alive = true;
    api.fetchFournisseurs(warehouseId)
      .then((list) => alive && setFournisseurs(Array.isArray(list) ? list : []))
      .catch(() => alive && setFournisseurs([]));
    return () => { alive = false; };
  }, [warehouseId]);

  const load = useCallback(
    async (p) => {
      const requestId = ++listRequestRef.current;
      const requestScope = JSON.stringify([user?.id, companyId, warehouseId, orderType]);
      // Le desktop ne fait aucun appel API sans magasin sélectionné.
      if (!warehouseId) { setRows([]); setTotal(0); setSummary(null); setLoading(false); return; }
      const f = filters.find((x) => x.key === filter) || filters[0];
      setLoading(true);
      try {
        const res = await api.fetchAchats({
          order_type: orderType,
          page: p,
          limit: PAGE,
          warehouse: warehouseId,
          company_id: companyId,
          ...(showPayments ? { include_summary: "true" } : {}),
          ...(appliedSearch.trim() ? { search: appliedSearch.trim() } : {}),
          ...(f.status ? { payment_status: f.status } : {}),
          ...(f.deleted ? { include_deleted: true } : {}),
        });
        const incoming = res.data || [];
        if (requestScope !== currentPageScope.current || requestId !== listRequestRef.current) return;
        setRows((prev) => (p === 1 ? incoming : [...prev, ...incoming]));
        setTotal(res.total || 0);
        setSummary(res.summary || null);
      } catch {
        if (requestScope === currentPageScope.current && requestId === listRequestRef.current) {
          message.error(`Impossible de charger les ${moduleLabelPlural}`);
        }
      } finally {
        if (requestScope === currentPageScope.current && requestId === listRequestRef.current) setLoading(false);
      }
    },
    [user?.id, warehouseId, companyId, orderType, showPayments, appliedSearch, filter, filters, moduleLabelPlural]
  );

  useEffect(() => {
    setPage(1);
    load(1);
    return () => { listRequestRef.current += 1; };
  }, [load]);

  const loadMore = () => { const p = page + 1; setPage(p); load(p); };
  const reload = () => { setPage(1); load(1); };

  const openDetail = async (order) => {
    setDetail(order);
    setItems([]); setPayments([]);
    setDetailLoading(true);
    try {
      const [full, lines, pays] = await Promise.all([
        api.fetchAchatDetail(order.id).catch(() => null),
        api.fetchOrderItems(order.id).catch(() => []),
        canViewPayments
          ? fetchOrderPayments(order.id, { companyId, warehouseId }).catch(() => [])
          : Promise.resolve([]),
      ]);
      if (full) setDetail((prev) => ({ ...prev, ...full }));
      setItems(Array.isArray(lines) ? lines : []);
      setPayments(pays || []);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setDetail(null); setItems([]); setPayments([]); };

  const onDownloadPdf = async (order) => {
    try {
      message.loading({ content: "Génération du PDF…", key: "orderPdf" });
      await downloadOrderPdf(order, orderType);
      message.success({ content: "PDF téléchargé avec succès", key: "orderPdf" });
    } catch {
      message.error({ content: "Impossible de télécharger le document.", key: "orderPdf" });
    }
  };

  const runDestructive = (order, kind) => {
    const spec = {
      restore: {
        title: `Restaurer ce ${moduleLabel} ?`, ok: "Oui, restaurer", danger: false,
        run: () => api.restoreAchat(order.id), done: "Commande restaurée avec succès",
      },
      delete: {
        title: `Supprimer ce ${moduleLabel} ?`, ok: "Oui, supprimer", danger: true,
        run: () => api.deleteAchat(order.id), done: "Commande supprimée avec succès",
      },
    }[kind];
    if (!spec) return;

    closeDetail();
    Modal.confirm({
      title: spec.title, content: spec.content, centered: true,
      okText: spec.ok, cancelText: "Non", okType: spec.danger ? "danger" : "primary",
      onOk: async () => {
        try { await spec.run(); void invalidatePurchases(); message.success(spec.done); reload(); }
        catch (error) {
          message.error(error instanceof Error ? error.message : "L'opération a échoué");
        }
      },
    });
  };

  const paid = detail ? num(detail.paid_amount) : 0;
  const totalTTC = detail ? num(detail.total) : 0;
  const destructive = detail ? destructiveActionFor(detail, canDelete, showPayments) : null;
  const extras = detail ? extraCostsOf(detail).filter((e) => e.amount > 0) : [];

  if (!warehouseId) {
    return (
      <ProductScreen>
        <Appbar title={moduleLabelPlural} onMenu={onMenu} />
        <div className="body">
          <EmptyState
            icon="warehouse" title="Aucun établissement sélectionné"
            text={`Choisissez un établissement dans la barre de navigation pour voir les ${moduleLabelPlural}.`}
          />
        </div>
      </ProductScreen>
    );
  }

  return (
    <ProductScreen>
      <Appbar
        title={moduleLabelPlural} onMenu={onMenu}
        subtitle={`${fmt(total)} ${total > 1 ? moduleLabelPlural : moduleLabel}`}
        action={
          otherTabs.length ? (
            <button className="iconbtn" aria-label="Aller à un autre onglet" onClick={() => setNavOpen(true)}>
              <Ico name="list-ordered" />
            </button>
          ) : null
        }
      />

      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        {summary ? (
          <Summary items={[
            { label: "Commandes", value: fmt(summary.total_purchase_count) },
            { label: "Payé", value: fmtMoney(summary.total_paid_amount) },
            { label: "Reste dû", value: fmtMoney(summary.total_due_amount) },
          ]} />
        ) : null}
        <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un numéro ou un fournisseur…" />
        <Chips value={filter} onChange={setFilter} options={filters.map((f) => ({ key: f.key, label: f.label }))} />
      </div>

      <div className="body">
        {loading && !rows.length ? (
          <EmptyState loading title="Chargement en cours" text="Synchronisation en cours." />
        ) : !rows.length ? (
          <EmptyState
            icon="scroll-text"
            title={appliedSearch || filter !== "all" ? "Aucun résultat" : `Aucun ${moduleLabel}`}
            text={appliedSearch || filter !== "all"
              ? "Aucune commande ne correspond à vos filtres."
              : `Les ${moduleLabelPlural} enregistrés apparaîtront ici.`}
          />
        ) : (
          <div className="list">
            {rows.map((o) => {
              const ord = orderPill(o);
              const pay = paymentPill(o.payment_status);
              return (
                <button key={o.id} className="rcard" onClick={() => openDetail(o)}>
                  <Mono name={o.invoice_number || "A"} />
                  <div className="rcard__main">
                    <div className="rcard__name">{o.invoice_number}</div>
                    <div className="rcard__meta">
                      {o.is_deleted === 1 ? <Pill tone="danger" label="Supprimé" /> : null}
                      {showPayments ? <Pill tone={pay.tone} label={pay.label} /> : null}
                      <Pill tone={ord.tone} label={ord.label} />
                    </div>
                    <div className="rcard__meta">
                      <span className="rcard__sub">{fmtDate(o.order_date)} · {supplierNameOf(o, fournisseurs)}</span>
                    </div>
                  </div>
                  <span className="rcard__price">
                    {fmtMoney(o.total)}
                    {showPayments ? <small> payé {fmtMoney(o.paid_amount)}</small> : null}
                  </span>
                </button>
              );
            })}

            {rows.length < total ? (
              <button className="btn btn--ghost" style={{ width: "100%" }} onClick={loadMore} disabled={loading}>
                <Ico name={loading ? "loader-circle" : "list-ordered"} className={loading ? "spin" : ""} />
                Charger plus ({rows.length}/{total})
              </button>
            ) : null}
          </div>
        )}
      </div>

      {/* feuille : navigation vers les autres onglets Achats */}
      <Sheet open={navOpen} onClose={() => setNavOpen(false)} title="Aller à…" subtitle="Autres écrans du module Achats.">
        {otherTabs.map((t) => (
          <ActionRow
            key={t.key} icon="chevron-right" label={t.label}
            onClick={() => { setNavOpen(false); navigate(t.path); }}
          />
        ))}
      </Sheet>

      {/* feuille : détail d'une commande */}
      <Sheet
        open={Boolean(detail)} onClose={closeDetail}
        title={detail?.invoice_number}
        subtitle={detail ? supplierNameOf(detail, fournisseurs) : undefined}
      >
        {detail ? (
          <>
            <DetailRow label="Date" value={fmtDate(detail.order_date)} />
            <DetailRow label="Établissement" value={detail.warehouse_name || "—"} />
            <DetailRow label="Statut" value={orderPill(detail).label} />
            {canViewPayments ? (
              <>
                <DetailRow label="Statut paiement" value={paymentPill(detail.payment_status).label} />
                <DetailRow label="Total" value={fmtMoney(totalTTC)} />
                <DetailRow label="Payé" value={fmtMoney(paid)} />
                <DetailRow label="Reste dû" value={fmtMoney(Math.max(0, totalTTC - paid))} />
              </>
            ) : (
              <DetailRow label="Total" value={fmtMoney(totalTTC)} />
            )}

            {extras.length ? (
              <>
                <div className="field__lbl" style={{ margin: "16px 0 8px" }}>
                  Frais annexes <small>(intégrés au coût de revient)</small>
                </div>
                {extras.map((e) => (
                  <DetailRow
                    key={e.key}
                    label={`${e.label} · ${e.included ? "inclus fournisseur" : "suivi séparé"}`}
                    value={fmtMoney(e.amount)}
                  />
                ))}
              </>
            ) : null}

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Articles</div>
            {detailLoading && !items.length ? (
              <EmptyState loading title="Chargement…" />
            ) : !items.length ? (
              <Note>Aucun article à afficher.</Note>
            ) : (
              items.map((it, i) => (
                <DetailRow
                  key={it.id ?? i}
                  label={it.product_name || it.name}
                  value={`${fmt(it.quantity)} × ${fmtMoney(it.unit_price)}`}
                />
              ))
            )}

            {showPayments ? (
              <>
                <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Paiements</div>
                {!payments.length ? (
                  <Note>Aucun paiement enregistré pour cette commande.</Note>
                ) : (
                  payments.map((p, i) => (
                    <DetailRow
                      key={p.id ?? i}
                      label={`${p.payment_number || `Paiement ${i + 1}`}${p.payment_mode_name ? ` · ${p.payment_mode_name}` : ""}`}
                      value={fmtMoney(p.amount)}
                    />
                  ))
                )}
              </>
            ) : null}

            <div style={{ marginTop: 16 }}>
              {canViewPayments && canCreatePayment && detail.is_deleted !== 1 && paid < totalTTC ? (
                <ActionRow icon="banknote" label="Ajouter un paiement" onClick={() => setPaymentFor(detail)} />
              ) : null}
              {hasPermission(perms.generatePdf) ? (
                <ActionRow icon="printer" label="Télécharger le PDF" onClick={() => onDownloadPdf(detail)} />
              ) : null}
              {destructive === "restore" ? (
                <ActionRow icon="arrow-left" label="Restaurer la commande" onClick={() => runDestructive(detail, "restore")} />
              ) : null}
              {destructive === "delete" ? (
                <ActionRow icon="trash-2" label="Supprimer la commande" danger onClick={() => runDestructive(detail, "delete")} />
              ) : null}
              {showPayments && detail.is_deleted !== 1 && paid > 0 && canDelete ? (
                <Note>
                  Un paiement est enregistré : utilisez un avoir ou un retour
                  fournisseur au lieu de modifier ou supprimer cet achat.
                </Note>
              ) : null}
              {hasPermission(perms.edit) && detail.is_deleted !== 1 && (!showPayments || paid === 0) ?
                <ActionRow icon="pencil" label={`Modifier ${moduleLabel}`} onClick={async () => {
                  try {
                        const requestId = ++editRequestRef.current;
                        const current = await api.fetchAchatDetail(String(detail.id));
                    const lines = current.produitsAches?.length ? current.produitsAches
                      : await api.fetchOrderItems(String(detail.id));
                    if (requestId !== editRequestRef.current) return;
                    closeDetail(); setFormFor({ ...current, items: lines });
                  } catch { message.error("Impossible de charger la commande à modifier."); }
                }} /> : null}
            </div>
          </>
        ) : null}
      </Sheet>

      {paymentFor && canViewPayments && canCreatePayment ? (
        <PaymentFormModal
          visible
          order={paymentFor}
          onClose={() => setPaymentFor(null)}
          onPaymentAdded={async () => { setPaymentFor(null); closeDetail(); reload(); }}
        />
      ) : null}
      {hasPermission(perms.create) && <Fab label={`Nouvel ${moduleLabel}`} onClick={() => setFormFor({})} />}
      {formFor && <PurchaseFormWorkspace key={formFor.id || "new"} order={formFor.id ? formFor : null} orderType={orderType}
        onClose={() => setFormFor(null)} onSaved={async () => { setFormFor(null); reload(); }} />}
    </ProductScreen>
  );
}
